<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import type { ApiClient } from '@xjlv2/api-client'
import {
  AdminFilterBar,
  AdminDataTable,
  AdminDrawer,
  AdminStatusTag,
  RrInput,
  RrSelect,
  RrButton,
  RrForm,
  RrFormItem,
  RrCheckbox,
  RrSpace,
  type TableColumnType
} from '@xjlv2/admin-layout'
import type {
  ClientRole,
  ClientUser,
  CreateClientUserRequest,
  PageResult,
  UpdateClientUserRequest
} from '../../types'

interface UserDraft {
  mode: 'create' | 'edit'
  userId: number | null
  username: string
  displayName: string
  mobile: string
  password: string
  enabled: boolean
  roleIds: number[]
  shopIdsText: string
}

const props = defineProps<{
  createApiClient: () => ApiClient
  roles: ClientRole[]
  canCreate: boolean
  canUpdate: boolean
}>()

const rawUsers = ref<ClientUser[]>([])
const total = ref(0)
const page = ref(1)
const pageSize = ref(20)
const loading = ref(false)

const filters = ref({ keyword: '', status: 'ALL' as 'ALL' | 'ACTIVE' | 'DISABLED' })
const showDraft = ref(false)
const draft = ref<UserDraft>(emptyDraft())
const parsedShopIds = computed(() => parseShopIds(draft.value.shopIdsText))
const shopScopeError = computed(() => parsedShopIds.value === null
  ? '店铺范围只能填写用英文逗号分隔的正整数 ID'
  : '')

async function fetchUsers() {
  loading.value = true
  try {
    const api = props.createApiClient()
    const params = new URLSearchParams({
      pageNo: String(page.value),
      pageSize: String(pageSize.value)
    })
    if (filters.value.keyword.trim()) params.set('keyword', filters.value.keyword.trim())
    if (filters.value.status !== 'ALL') params.set('status', filters.value.status)
    const res = await api.get<PageResult<ClientUser>>(`/api/saas/system/client-rbac/users?${params.toString()}`)
    rawUsers.value = res.records
    total.value = res.total
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  void fetchUsers()
})

watch([page, pageSize], () => {
  void fetchUsers()
})

watch([() => filters.value.keyword, () => filters.value.status], () => {
  page.value = 1
  void fetchUsers()
})

function emptyDraft(): UserDraft {
  const defaultRole = props.roles.find((role) => role.code === 'client_member') ?? props.roles[0]
  return {
    mode: 'create',
    userId: null,
    username: 'client-user-001',
    displayName: 'C端用户',
    mobile: '',
    password: 'client123',
    enabled: true,
    roleIds: defaultRole ? [defaultRole.id] : [],
    shopIdsText: ''
  }
}

function roleName(roleId: number) {
  return props.roles.find((role) => role.id === roleId)?.name ?? `角色 ${roleId}`
}

function openCreate() {
  draft.value = emptyDraft()
  showDraft.value = true
}

function openEdit(user: ClientUser) {
  draft.value = {
    mode: 'edit',
    userId: user.id,
    username: user.username,
    displayName: user.displayName,
    mobile: user.mobile,
    password: '',
    enabled: user.enabled,
    roleIds: [...user.roleIds].sort((left, right) => left - right),
    shopIdsText: [...user.shopIds].sort((left, right) => left - right).join(',')
  }
  showDraft.value = true
}

function toggleRole(roleId: number) {
  const selected = new Set(draft.value.roleIds)
  if (selected.has(roleId)) selected.delete(roleId)
  else selected.add(roleId)
  draft.value.roleIds = Array.from(selected).sort((left, right) => left - right)
}

function parseShopIds(value: string): number[] | null {
  const normalized = value.trim()
  if (!normalized) return []
  const parts = normalized.split(',').map((part) => part.trim())
  if (parts.some((part) => !/^[1-9]\d*$/.test(part))) return null
  const values = parts.map(Number)
  if (values.some((shopId) => !Number.isSafeInteger(shopId))) return null
  return Array.from(new Set(values)).sort((left, right) => left - right)
}

async function submit() {
  const shopIds = parsedShopIds.value
  if (!draft.value.username.trim() || !draft.value.displayName.trim() ||
    draft.value.roleIds.length === 0 || shopIds === null) return
  const payload = {
    displayName: draft.value.displayName.trim(),
    mobile: draft.value.mobile.trim(),
    password: draft.value.password.trim(),
    enabled: draft.value.enabled,
    roleIds: draft.value.roleIds,
    shopIds
  }
  loading.value = true
  try {
    const api = props.createApiClient()
    if (draft.value.mode === 'create') {
      await api.post<ClientUser>('/api/saas/system/client-rbac/users', { username: draft.value.username.trim(), ...payload })
    } else if (draft.value.userId != null) {
      await api.put<ClientUser>(`/api/saas/system/client-rbac/users/${draft.value.userId}`, payload)
    }
    showDraft.value = false
    await fetchUsers()
  } finally {
    loading.value = false
  }
}

const columns: TableColumnType<ClientUser>[] = [
  { key: 'user', title: '用户' },
  { key: 'mobile', title: '手机号' },
  { key: 'roles', title: 'C端角色' },
  { key: 'shops', title: '店铺范围' },
  { key: 'status', title: '状态' },
  { key: 'updatedAt', title: '更新时间' },
  { key: 'actions', title: '操作' }
]

const statusOptions = [
  { label: '全部状态', value: 'ALL' },
  { label: '启用', value: 'ACTIVE' },
  { label: '停用', value: 'DISABLED' }
]

const enabledOptions = [
  { label: '启用', value: 'true' },
  { label: '停用', value: 'false' }
]
</script>

<template>
  <section class="panel span management-list">
    <div class="panel-head">
      <div>
        <p class="eyebrow">Client User</p>
        <h2>C端用户</h2>
        <small>管理 sys_client_user 登录状态与 sys_client_user_role 授权</small>
      </div>
      <span>共 {{ total }} 个用户</span>
    </div>

    <AdminFilterBar
      :loading="loading"
      @reset="filters = { keyword: '', status: 'ALL' }"
    >
      <label>
        关键字
        <RrInput v-model:value.trim="filters.keyword" autocomplete="off" placeholder="账号、名称、手机号" />
      </label>
      <label>
        状态
        <RrSelect v-model:value="filters.status" :options="statusOptions" />
      </label>
      <template #actions>
        <RrButton type="default" :disabled="loading" @click="filters = { keyword: '', status: 'ALL' }">重置</RrButton>
        <RrButton type="primary" :disabled="loading || !canCreate || roles.length === 0" @click="openCreate">新增 C 端用户</RrButton>
      </template>
    </AdminFilterBar>

    <AdminDrawer
      v-model:open="showDraft"
      :title="draft.mode === 'create' ? '新增 C 端用户' : '编辑用户与授权'"
      :width="640"
      @close="showDraft = false"
    >
      <RrForm layout="vertical" :disabled="loading || (draft.mode === 'create' ? !canCreate : !canUpdate)" @submit.prevent="submit">
        <div class="form-grid inline-form-grid">
          <RrFormItem label="登录账号">
            <RrInput v-model:value.trim="draft.username" autocomplete="off" :disabled="draft.mode === 'edit'" />
          </RrFormItem>
          <RrFormItem label="用户名称">
            <RrInput v-model:value.trim="draft.displayName" autocomplete="off" />
          </RrFormItem>
          <RrFormItem label="手机号">
            <RrInput v-model:value.trim="draft.mobile" autocomplete="off" />
          </RrFormItem>
          <RrFormItem :label="draft.mode === 'create' ? '登录密码' : '重置密码'">
            <RrInput v-model:value="draft.password" type="password" autocomplete="new-password" :placeholder="draft.mode === 'edit' ? '留空表示不修改' : ''" />
          </RrFormItem>
          <RrFormItem label="账号状态">
            <RrSelect v-model:value="draft.enabled" :options="enabledOptions" />
          </RrFormItem>
          <RrFormItem label="店铺范围">
            <RrInput v-model:value="draft.shopIdsText" autocomplete="off" placeholder="11001,11002" />
            <small v-if="shopScopeError" class="field-validation-error" style="color: var(--rr-color-danger)">{{ shopScopeError }}</small>
          </RrFormItem>
        </div>

        <div class="permission-config-block" style="margin-top: 16px;">
          <div class="section-head" style="margin-bottom: 8px;">
            <h3 style="margin: 0; font-size: 14px;">C端角色授权</h3>
            <span style="font-size: 12px; color: var(--rr-color-text-muted); margin-left: 8px;">{{ draft.roleIds.length }} 已选</span>
          </div>
          <div class="check-grid compact-check-grid" style="display: grid; grid-template-columns: 1fr 1fr; gap: 8px;">
            <RrCheckbox
              v-for="role in roles"
              :key="role.id"
              :checked="draft.roleIds.includes(role.id)"
              @update:checked="toggleRole(role.id)"
            >
              <span><strong>{{ role.name }}</strong><small style="margin-left:4px;color:var(--rr-color-text-muted)">{{ role.code }} · {{ role.permissions.length }} 个权限</small></span>
            </RrCheckbox>
          </div>
        </div>
      </RrForm>

      <template #footer>
        <RrSpace>
          <RrButton @click="showDraft = false">取消</RrButton>
          <RrButton
            type="primary"
            :disabled="loading || draft.roleIds.length === 0 || parsedShopIds === null || (draft.mode === 'create' ? !canCreate : !canUpdate)"
            @click="submit"
          >
            保存用户与授权
          </RrButton>
        </RrSpace>
      </template>
    </AdminDrawer>

    <AdminDataTable
      :columns="columns"
      :data-source="rawUsers"
      :loading="loading"
      :current="page"
      :page-size="pageSize"
      :total="total"
      row-key="id"
      :count="rawUsers.length"
      :empty="total === 0"
      empty-title="暂无匹配的 C 端用户"
      @update:current="page = $event"
      @update:page-size="pageSize = $event"
    >
      <template #bodyCell="{ column, record }">
        <template v-if="column.key === 'user'">
          <span><strong>{{ record.displayName }}</strong><small style="margin-left:4px;color:var(--rr-color-text-muted)">{{ record.username }} · ID {{ record.id }}</small></span>
        </template>
        <template v-else-if="column.key === 'mobile'">
          {{ record.mobile || '-' }}
        </template>
        <template v-else-if="column.key === 'roles'">
          {{ record.roleIds.map(roleName).join('、') || '未授权' }}
        </template>
        <template v-else-if="column.key === 'shops'">
          {{ [...record.shopIds].sort((left, right) => left - right).join('、') || '未配置' }}
        </template>
        <template v-else-if="column.key === 'status'">
          <AdminStatusTag :tone="record.enabled ? 'success' : 'neutral'">{{ record.enabled ? '启用' : '停用' }}</AdminStatusTag>
        </template>
        <template v-else-if="column.key === 'updatedAt'">
          {{ record.updatedAt?.replace('T', ' ').slice(0, 19) || '-' }}
        </template>
        <template v-else-if="column.key === 'actions'">
          <RrButton type="default" :disabled="loading || !canUpdate" @click="openEdit(record)">编辑授权</RrButton>
        </template>
      </template>
    </AdminDataTable>
  </section>
</template>
